import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, NormalizedResponse, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
//import * as moment from 'moment';
import moment from 'moment';
import { ONXEligibilityResponseApplicant, ApplicantGrouping, EligibilityRenderable, EligibilityResponsePayload } from '../interfaces/eligibility.interface';
import { eligibilityND, eligibilityWY } from './eligibility-processor.config';

@Injectable({
  providedIn: 'root'
})
export class EligibilityProcessorService {
  /* MOCK - DO NOT COMMIT */
  forceValues = false;

  AEPFlag = true;
  ineligibleApplicants = false;
  APTCFlag = true;
  CSRFlag = false;
  eligibilityInfo: EligibilityRenderable;

  // Sets the hieratchy of all CSR Levels, as they are not in numerical order from least to greatest
  CSRLevelConfig = {
    '01': 1,
    '03': 2,
    '04': 3,
    '05': 4,
    '06': 5,
    '02': 6
  };
  relationshipCodes = {
    allCodes: {
      '01': 'Spouse',
      '03': { M: 'Father', F: 'Mother' },
      '04': { M: 'Grandfather', F: 'Grandmother' },
      '05': { M: 'Grandson', F: 'Granddaughter' },
      '06': { M: 'Uncle', F: 'Aunt' },
      '07': { M: 'Nephew', F: 'Niece' },
      '08': 'Cousin',
      '09': 'Adopted Child',
      '10': 'Foster Child',
      '11': { M: 'Son-in-law', F: 'Daughter-in-law' },
      '12': { M: 'Brother-in-law', F: 'Sister-in-law' },
      '13': { M: 'Father-in-law', F: 'Mother-in-law' },
      '14': { M: 'Brother', F: 'Sister' },
      '15': 'Ward',
      '16': 'Stepparent',
      '17': { M: 'Stepson', F: 'Stepdaughter' },
      '18': 'Self',
      '19': 'Child',
      '23': 'Sponsored Dependent',
      '24': 'Dependent of a Minor Dependent',
      '25': 'Ex-spouse',
      '26': 'Guardian',
      '31': 'Court Appointed Guardian',
      '38': 'Collateral Dependent',
      '53': 'Life Partner',
      '60': 'Annuitant',
      D2: 'Trustee',
      G8: 'Other Relationship',
      G9: 'Other Relative'
    },
    WV: ['01', '09', '17', '18', '19', '53'],
    DE: ['01', '09', '15', '17', '18', '19', '23', '53'],
    PA: ['01', '09', '15', '17', '18', '19', '53'],
    ND: ['01', '09', '15', '17', '18', '19', '53'],
    WY: ['01', '09', '15', '17', '18', '19', '53'],
    DependentCodes: ['09', '17', '19', '23']
  };

  eligibilityContent: any;

  constructor(
    private brandService: BrandingConfigurationService,
    private httpClient: HTTPWrapperContext
  ) { }

  getEligibilityContent() {
    let curBrandCode = this.brandService.getCurrentBrand().cookieBrandCode?.toUpperCase();
    if (curBrandCode === 'NDBCBS') {
      this.eligibilityContent = eligibilityND;
    }
    if (curBrandCode === 'WYBCBS') {
      this.eligibilityContent = eligibilityWY;
    }
    return this.eligibilityContent;
  }
  async fetchEligibility(prospectId: string): Promise<NormalizedResponse<EligibilityResponsePayload>> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: '/cmcrst/x-services/eligibility/applicant',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      GET_PAYLOAD: {
        prospectId
      }
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);

    const mockResponse: NormalizedResponse<EligibilityResponsePayload> = {
      baseMessage: {
        uuid: '1D4E3387841E458E9A4ADAEC5E8CC842',
        returnCode: 200,
        returnCodeDescription: 'ELIGIBILITY_SUCCESS'
      },
      payload: {
        APTC: false,
        applicationStatus: 'Completed Eligibility Application',
        CSR: false,
        multiTaxHouse: false,
        customerData: {
          fullName: { firstName: 'HELEN', lastName: 'GAWTHORP-MACKE' },
          homeAddress: {
            address: '44 Fair VW S',
            city: 'Fairview',
            state: 'WY',
            county: 'LINCOLN',
            zipCode: '83119'
          },
          phoneNumber: '123-123-1234',
          emailAddress: 'vmxfnoradqyepfwnid@kvhrw.com',
          ssn: ''
        },
        applicants: [
          {
            personID: 'Person1',
            fullName: { firstName: 'HELEN', lastName: 'GAWTHORP-MACKE' },
            homeAddress: {
              address: '44 Fair VW S',
              city: 'Fairview',
              state: 'WY',
              county: 'LINCOLN',
              zipCode: '83119'
            },
            dateOfBirth: '01/01/1970',
            gender: 'F',
            APTCPrice: 127.41,
            QHP: true,
            IEP: false,
            AEP: true,
            SEP: false,
            CHIP: false,
            medicaid: false,
            APTC: true,
            CSR: false,
            familyCodes: [{ codeValue: '18', person: 'Person1' }],
            FFEUserID: '18715235',
            newMember: false,
            lastUpdatedDate: '1969-12-31',
            ffmAppId: '2360672863547532683',
            primaryContact: false,
            ssn: '121-12-6212',
            ichraIndicator: false,
            ichraAffordabilityIndicator: false,
            qsehraIndicator: false
          }
        ],
        primaryTaxFilerPersonReference: {
          fullName: { firstName: 'HELEN', lastName: 'GAWTHORP-MACKE' },
          personId: 'Person1'
        }
      }
    };

    return (this.forceValues ?
      mockResponse :
      this.httpClient.makeRequest<NormalizedResponse<EligibilityResponsePayload>>(requestConfigImplementation)
    );

  }

  // Start of the flow, sort by QHP Eligiblility.
  private sortApplicantsByQHP(applicants: ONXEligibilityResponseApplicant[], applicantGrouping) {
    // Check all applicants for QHP eligibility, run additional logic based upon path, then sort them by group
    applicants.forEach(applicant => {
      this.QHPEligible(applicant);

      if (applicant.isEligible) {
        applicantGrouping.eligible.push(applicant);
      } else {
        applicantGrouping.ineligible.push(applicant);
        this.ineligibleApplicants = true;
      }
    });
  }

  // Check applicant for QHP eligibility
  private QHPEligible(applicant: ONXEligibilityResponseApplicant) {
    if (applicant.QHP && !applicant.CHIP && !applicant.medicaid) {
      // Since they are QHP eligible, send them to the AEP check.
      applicant.isEligible = true;
      this.AEPEligible(applicant);
    } else {
      // Set region specific links for Medicaid and CHIP
      applicant.isEligible = false;
      this.setRegionLinks(applicant);
    }
  }

  // Set user messages defined in the config to set region specific links.
  private setRegionLinks(applicant: ONXEligibilityResponseApplicant) {
    applicant.medicaidLink = this.getEligibilityContent().content.medicaid;
    applicant.CHIPLink = this.getEligibilityContent().content.CHIP;
  }

  private AEPEligible(applicant: ONXEligibilityResponseApplicant) {
    if ((!applicant.AEP && !applicant.IEP) || applicant.SEP) {
      this.AEPFlag = false;
    }
  }

  private APTCsumCalc(applicantGrouping: ApplicantGrouping) {
    let APTCtotal = 0;

    applicantGrouping.eligible.forEach(applicant => {
      if (applicant.APTC) {
        APTCtotal += applicant.APTCPrice;
      }
    });

    if (APTCtotal > 0) {
      this.APTCFlag = true;
    } else {
      this.APTCFlag = false;
    }

    return APTCtotal;
  }

  /** Qualified Small Employer Health Reimbursement Arrangement (QSEHRA) */
  private isQSEHRA(applicant: ONXEligibilityResponseApplicant) {
    if (applicant.qsehraIndicator) {
      return true;
    }
    return false;
  }

  /**  Individual Coverage HRA (ICHRA) */
  private isICHRA(applicant: ONXEligibilityResponseApplicant) {
    if (applicant.ichraIndicator) {
      return true;
    }
    return false;
  }

  private isQSEHRAorICHRA(applicant: ONXEligibilityResponseApplicant) {
    const returnable = this.isICHRA(applicant) || this.isQSEHRA(applicant);
    console.log('returnable is ' + returnable)
    return returnable;
  }

  // Loops through all QHP eligible applicants to determine if they can be a primary applicant
  private setPrimaryApplicants(applicantGrouping: ApplicantGrouping, primaryApplicants: ONXEligibilityResponseApplicant[]) {
    applicantGrouping.eligible.forEach(applicant => {
      this.primaryEligible(applicant);
      if (applicant.primaryEligible) {
        // Add applicant to primary applicants array
        primaryApplicants.push(applicant);
      }
    });
  }

  // Determines if applicant can be a primary applicant based upon their relationships to other applicants
  private primaryEligible(applicant: ONXEligibilityResponseApplicant) {
    // Make sure the applicant has family codes and hasn't already had their primary eligibilty set
    if (
      applicant.familyCodes &&
      applicant.primaryEligible !== false &&
      (applicant.personID === 'Person1' || applicant.personID === 'person1')
    ) {
      // Set primaryEligible to true to begin with
      applicant.primaryEligible = true;
      // Loop through family codes
      applicant.familyCodes.some(relationship => {
        // If the relationship is not QHP eligible, it doesn't count against the applicants primary eligibility
        if (this.isRelationshipQHPEligilble(relationship.person)) {
          // Check if, based upon the state, the relationship is an allowed relationship for a primary applicant
          if (this.relationshipCodes[applicant.homeAddress.state].indexOf(relationship.codeValue) === -1) {
            applicant.primaryEligible = false;
            return true;
          }
        }
      });
    } else {
      applicant.primaryEligible = false;
    }
  }

  // Checks if the passed personId is QHP Eligible
  private isRelationshipQHPEligilble(personId: string) {
    const person = this.getEligibleApplicantByPersonId(this.eligibilityInfo, personId);
    return person ? true : false;
  }

  // Look at all dependent's ages, if any are over 25, then the family is not supported
  hasDependentsOver25(eligibilityData: EligibilityRenderable, effectiveDate?: string) {
    // First, get all dependents
    const dependents = this.getDependents(eligibilityData);
    let unsupportedFamily: boolean;

    const eDate = effectiveDate ? effectiveDate : eligibilityData.applicants[0].effectiveDate;
    const currentBrand = this.brandService.getCurrentBrand();
    // Loop through dependents and check their age
    dependents.some(dependent => {

      const isOver25 = moment(eDate).diff(moment(dependent.dateOfBirth), 'year') > 25;
      if (isOver25) {
        unsupportedFamily = true;
        return true;
      }
    });

    return unsupportedFamily;
  }

  // Gets dependents of default primary applicant
  private getDependents(eligibilityData: EligibilityRenderable) {
    const dependents = [];
    // Loop through the default primary applicant's family codes so can determine which members are dependents
    eligibilityData.primaryApplicants[0].familyCodes.forEach(familyCode => {
      // If this family code is one of the dependent codes, then add depdent to array
      if (this.relationshipCodes.DependentCodes.indexOf(familyCode.codeValue) >= 0) {
        const dependent = this.getEligibleApplicantByPersonId(eligibilityData, familyCode.person);
        if (dependent) {
          dependents.push(dependent);
        }
      }
    });
    return dependents;
  }

  // Gets the applicant from eligibility data with the matching personId
  private getEligibleApplicantByPersonId(eligibilityData: EligibilityRenderable, personId: string) {
    let applicant: ONXEligibilityResponseApplicant;
    eligibilityData.processedEligibilityResults.eligible.some(eligibleApplicant => {
      if (eligibleApplicant.personID === personId) {
        applicant = eligibleApplicant;
        return true;
      }
    });
    return applicant;
  }

  private checkCSR(dataObject: EligibilityRenderable) {
    // Initially set level to highest, then loop through and set to any that are lower than current
    let storedCSR;
    dataObject.CSRLevel = '02';
    dataObject.CSRMessage = false;

    /** Force type assertion, typescript build doesn't quite understand the the value COULD change inside of the foreach */
    let allAreCSRTrue = true as boolean; // assume all are true, set to false if anyone is false;


    dataObject.processedEligibilityResults.eligible.forEach((user: ONXEligibilityResponseApplicant, count: number) => {

      if (user.CSR === true) {
        this.CSRFlag = true;
      } else {
        allAreCSRTrue = false;
      }

      if (count === 0) {
        storedCSR = this.CSRLevelConfig[user.CSRLevel];
      } else if (count > 0) {
        if (storedCSR !== this.CSRLevelConfig[user.CSRLevel]) {
          dataObject.CSRMessage = true;
        }
      }

      if (this.CSRLevelConfig[dataObject.CSRLevel] > this.CSRLevelConfig[user.CSRLevel]) {
        dataObject.CSRLevel = user.CSRLevel;
      }
    });

    if (allAreCSRTrue === false) {
      dataObject.CSRLevel = '01';
      this.CSRFlag = false; // ensure flag is false
    }

    dataObject.CSR = this.CSRFlag;
  }


  // Determines which applicants are eligible to apply
  // Expects an array of applicants and returns an object
  // of eligible and ineligible applicants
  processEligibility(eligibilityData: EligibilityRenderable): EligibilityRenderable {

    console.log('eligibilityData.messageStatus' + eligibilityData.messageStatus);
    this.eligibilityInfo = eligibilityData;

    // Default messageStatus to QHPEligible if it is empty
    if (
      eligibilityData.messageStatus === '' ||
      eligibilityData.messageStatus === null ||
      eligibilityData.messageStatus === undefined
    ) {
      eligibilityData.messageStatus = 'QHPEligible';
    }

    // Check for MultiTax household, set appropriate message and cease processing
    if (eligibilityData.multiTaxHouse) {
      eligibilityData.messageStatus = 'MultiTaxHousehold';
      eligibilityData.multiTaxHouse = true;
      return eligibilityData;
    }

    const applicants = eligibilityData.applicants;
    const applicantGrouping: ApplicantGrouping = {
      eligible: [],
      ineligible: []
    };
    eligibilityData.primaryApplicants = [];

    console.log('Place 1' + eligibilityData.primaryApplicants.length);
    console.log('THe applicants' + applicants.length);
    // First, determine which applicants are QHP eligible by sorting them into two groups

    // force aptc data
    this.sortApplicantsByQHP(applicants, applicantGrouping);

    console.log('QHP eligibile applicants ' + applicants.length);
    eligibilityData.ineligibleApplicants = this.ineligibleApplicants;

    // Set sorted applicants on data object
    eligibilityData.processedEligibilityResults = applicantGrouping;

    console.log(' eligibilityData.primaryApplicants' + eligibilityData.primaryApplicants.length);

    // Determine which QHP eligible applicants can be primary applicants
    this.setPrimaryApplicants(applicantGrouping, eligibilityData.primaryApplicants);

    // Calculate aggregate ATPC values
    eligibilityData.APTCMonthly = this.APTCsumCalc(applicantGrouping).toFixed(2);
    eligibilityData.APTCYearly = (Number(eligibilityData.APTCMonthly) * 12).toFixed(2);

    this.checkCSR(eligibilityData);
    eligibilityData.APTC = this.APTCFlag;

    /**
     * Due not having approvals at time of development,
     * we created a contingency service call that can display data based on those approvals.
     */
    if (eligibilityData.messageStatus === 'contingency') {
      return eligibilityData;
    }

    // If no applicants are QHP Eligible or IEP/AEP flag is true, set appropriate message and cease processing
    if (applicantGrouping.eligible.length === 0) {
      eligibilityData.messageStatus = 'QHPIneligible';
      eligibilityData.QHPIneligible = true;
      return eligibilityData;
    }

    // If there are no primary applicants or there are more than 2, set appropriate message and cease processing
    console.log('Primary applicants' + eligibilityData.primaryApplicants.length);
    if (eligibilityData.primaryApplicants.length > 2) {
      eligibilityData.messageStatus = 'errorApp';
      eligibilityData.errorApp = true;
      return eligibilityData;
    }

    // If the AEP is not set, then the family is unsupported - this happens from a combination of AEP and SEP.
    if (!this.AEPFlag) {
      eligibilityData.messageStatus = 'SEPfamily';
      eligibilityData.SEPfamily = true;
      return eligibilityData;
    }

    // Check for any dependents over the age of 25, which will result in an unsupported family type
    if (this.hasDependentsOver25(eligibilityData)) {
      eligibilityData.messageStatus = 'unsupportedFam';
      eligibilityData.unsupportedFam = true;
    }

    /** Check if ANY applicant is enrolling in a QSEHRA or ICHRA */
    if (eligibilityData.applicants.some(applicant => this.isQSEHRAorICHRA(applicant))) {
      eligibilityData.QSEHRAorICHRA = true;
    }

    if (this.hasDependentsOver25(eligibilityData)) {
      eligibilityData.messageStatus = 'over25';
      eligibilityData.over25 = true;
    }

    return eligibilityData;
  }

}
